import React, { Fragment, useEffect, useState } from 'react';
import { Col, Container, Input, Label, Row } from 'reactstrap';
import { toast } from "react-toastify";
import { ServiceThreadSpinner } from '.';
import { api, helpers } from '../utils';

/* ===== Limits / Caps ===== */
const MECH_CAP_RPM = 7800;      // typical mechanical cap
const HARD_CAP_RPM = 8000;      // absolute mechanical cap
const BALLOON_C_LIGHT = 285000; // RPM_balloon = C / sqrt(totalDen)
const BALLOON_C_HEAVY = 288000; // RPM_balloon = C / sqrt(totalDen)
const HEAVY_CABLE_CAP = 3500;   // applies if plies >= 5
/* ===== Factors / Structs ===== */
const IN_PER_M = 39.37;         // Inches per meter
const YARDS_FACTOR = 4464528;
const STEP_INIT = {
   active1: true,
   active2: false,
   active3: false,
   activeLimit1: "",
   activeLimit2: "",
   activeLimit3: "",
   actualPPH1: 0,
   actualPPH2: 0,
   actualPPH3: 0,
   actualPPHperShift1: 0,
   actualPPHperShift2: 0,
   actualPPHperShift3: 0,
   balloonLimit1: 0,
   balloonLimit2: 0,
   balloonLimit3: 0,
   cableCap1: 0,
   cableCap2: 0,
   cableCap3: 0,
   conf1: { level: "green", label: "Within range" },
   conf2: { level: "green", label: "Within range" },
   conf3: { level: "green", label: "Within range" },
   denier1: 1000,
   denier2: 1000,
   denier3: 1000,
   efficiency1: 0.85,
   efficiency2: 0.85,
   efficiency3: 0.85,
   isCapped1: false,
   isCapped2: false,
   isCapped3: false,
   nonStopPPH1: 0,
   nonStopPPH2: 0,
   nonStopPPH3: 0,
   ply1: 1,
   ply2: 1,
   ply3: 1,
   rpmBase1: 0,
   rpmBase2: 0,
   rpmBase3: 0,
   rpmFinal1: 0,
   rpmFinal2: 0,
   rpmFinal3: 0,
   spindles1: 45,
   spindles2: 45,
   spindles3: 45,
   takeUpSpeed1: 0,
   takeUpSpeed2: 0,
   takeUpSpeed3: 0,
   tensioner1: "",
   tensioner2: "",
   tensioner3: "",
   totalDenier1: 1000,
   totalDenier2: 1000,
   totalDenier3: 1000,
   totalDenierContracted1: 1030,
   totalDenierContracted2: 1030,
   totalDenierContracted3: 1030,
   totalPPH1: 0,
   totalPPH2: 0,
   totalPPH3: 0,
   tpi1: 1.5,
   tpi2: 1.5,
   tpi3: 1.5,
   tpm1: 60,
   tpm2: 60,
   tpm3: 60,
   twistContraction: 1.03, // Add 3%
   yardsPerPound1: 0,
   yardsPerPound2: 0,
   yardsPerPound3: 0
};

const clamp = (x, l, h) => Math.max(l, Math.min(h, x));  // x = value to check, l = lowest minimum, h = highest maximum

function balloonLimitLight(totalDen) {
   const d = Math.max(1, totalDen);
   return BALLOON_C_LIGHT / Math.sqrt(d);
}

function balloonLimitHeavy(totalDen) {
   const d = Math.max(1, totalDen);
   return BALLOON_C_HEAVY / Math.sqrt(d);
}

/* ===== Single yarn RPM model (stable curve) ===== */
function rpmSingle(baseDen, tpi) {
   const d = clamp(baseDen, 420, 15000);
   const t = clamp(tpi, 0.8, 13.0);
   const rpm = Math.exp(8.99941691) * Math.pow(d, -0.02368227) * Math.pow(t, -0.02485566);
   return clamp(rpm, 1500, MECH_CAP_RPM);
}

/* ===== Multi-plies yarn RPM model ===== */
// Conservative / aggressive offsets for light flyer band.
// These are deliberately simple, transparent "band" adjustments
// for plant practice rather than pretending a single deterministic curve
// can explain all observed 2-4 ply operating choices.
//---------------------------------------------------------
// 2-4 ply expected from refit
function lightFlyerTensionerLimit(totalDen, tpi, mode) {
   const d = clamp(totalDen, 420, 15000);
   const t = clamp(tpi, 0.8, 13.0);
   let rpm = 7200 * Math.pow(d / 1000, -0.15) * Math.pow(t / 2.0, -0.10);
   if (mode === "conservative") rpm *= 0.92;
   if (mode === "aggressive") rpm *= 1.08;
   return rpm;
}

function rpmFlyerLight(totalDen, tpi, mode) {
   const physics = lightFlyerTensionerLimit(totalDen, tpi, mode);
   const limBalloon = balloonLimitLight(totalDen);
   const limMech = MECH_CAP_RPM;
   const final = clamp(Math.min(physics, limBalloon, limMech), 1500, HARD_CAP_RPM);

   const limits = [
      ["Physics", physics],
      ["Balloon limit", limBalloon],
      ["Mechanical cap", limMech],
   ].sort((a, b) => a[1] - b[1]);

   return {
      final,
      physics,
      limBalloon,
      limMech,
      limCable: Infinity,
      activeLimit: limits[0][0],
      isCapped: limits[0][0] !== "Physics",
   };
}

// 5+ ply heavy from refit
function heavyFlyerPhysics(totalDen, tpi) {
   const d = clamp(totalDen, 420, 15000);
   const t = clamp(tpi, 0.8, 13.0);
   return Math.exp(12.12712202) * Math.pow(d, -0.46068933) * Math.pow(t, 0.06241929);
}

function rpmFlyerHeavy(totalDen, tpi) {
   const physics = heavyFlyerPhysics(totalDen, tpi);
   const limBalloon = balloonLimitHeavy(totalDen);
   const limMech = MECH_CAP_RPM;
   const limCable = HEAVY_CABLE_CAP;
   const final = clamp(Math.min(physics, limBalloon, limMech, limCable), 1500, HARD_CAP_RPM);

   const limits = [
      ["Physics", physics],
      ["Balloon limit", limBalloon],
      ["Mechanical cap", limMech],
      ["Cable cap", limCable],
   ].sort((a, b) => a[1] - b[1]);

   return {
      final,
      physics,
      limBalloon,
      limMech,
      limCable,
      activeLimit: limits[0][0],
      isCapped: limits[0][0] !== "Physics",
   };
}

function rpmFlyer(totalDen, tpi, plies, flyerMode) {
   return plies <= 4
      ? rpmFlyerLight(totalDen, tpi, flyerMode)
      : rpmFlyerHeavy(totalDen, tpi);
}

/* ===== Yarn RPM model ===== */
function rpmResult(tensioner, denPerEnd, denTotal, tpi, plies, mode) {
   if (tensioner === "single") {
      const final = rpmSingle(denPerEnd, tpi);
      return {
         final,
         physics: final,
         limBalloon: Infinity,
         limMech: MECH_CAP_RPM,
         limCable: Infinity,
         activeLimit: "Physics",
         isCapped: false,
      };
   }
   return rpmFlyer(denTotal, tpi, plies, mode);
};

function confidence(result) {
   const limits = [result.limBalloon, result.limMech, result.limCable].filter(
      (x) => Number.isFinite(x) && x > 0 && x < 1e9
   );
   const nearest = limits.length ? Math.min(...limits) : Infinity;
   const frac = nearest === Infinity ? 0 : result.final / nearest;

   if (result.isCapped) return { level: "red", label: "Range capped" };
   if (frac >= 0.9) return { level: "yellow", label: "Near a limit" };
   return { level: "green", label: "Within range" };
}

function Badge({ level, text }) {
   const bg = level === "green" ? "#e8f7ee" : level === "yellow" ? "#fff6db" : "#ffe5e5";
   const bd = level === "green" ? "#b7e3c8" : level === "yellow" ? "#f2dc9a" : "#f0b0b0";
   const dot = level === "green" ? "#2e8b57" : level === "yellow" ? "#c79200" : "#b00020";
   return (
      <span
         style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 10px",
            borderRadius: 999,
            background: bg,
            border: `1px solid ${bd}`,
            fontSize: 12,
         }}
      >
         <span style={{ width: 8, height: 8, borderRadius: 99, background: dot }} />
         <span className="text-black">{text}</span>
      </span>
   );
}

export default function TwistingCalcs() {
   const mode = "expected"; // "conservative" | "aggressive" | "expected" (engineering/most accurate)
   const [model, setDefaults] = useState(null);
   const [pct, setPct] = useState(0.03);
   const [spin, setSpinner] = useState(true);
   const [steps, setSteps] = useState(STEP_INIT);

   useEffect(() => {
      GetTwistDefaults();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (model != null) {
         if (model?.twistContraction > 0) setPct(model.twistContraction);
         let den = model?.baseYarn > 0 ? model.baseYarn : 1000;
         let eff = model?.efficiency > 0 ? model.efficiency : 0.85;
         let ply = model?.ply > 0 ? model.ply : 1;
         let spindles = model?.spindles > 0 ? model.spindles : 45;
         let tpi = model?.tpi > 0 ? model.tpi : 1.5;
         let tpm = tpi * IN_PER_M;
         let contraction = model?.twistContraction > 0 ? (1 + model.twistContraction) : 1.03;
         let totalDen = den * ply;
         let totalDenCont = totalDen * contraction;
         let yardsLb = YARDS_FACTOR / totalDenCont;
         let tensionType = ply > 1 ? "flyer" : "single";
         let rpmRtn = rpmResult(tensionType, den, totalDen, tpi, ply, mode);
         let rpmFinal = rpmRtn.final;
         let conf = confidence(rpmRtn);
         let takeUp = ((2 * rpmFinal) / tpm) / 1.094;
         let nonStop = (takeUp * 60) / yardsLb;
         let bLimit = Number.isFinite(rpmRtn.limBalloon) ? rpmRtn.limBalloon.toFixed(0) : "-";
         let cap = Number.isFinite(rpmRtn.limCable) && rpmRtn.limCable < 1e9 ? rpmRtn.limCable.toFixed(0) : "-";

         let init = Object.assign({}, steps);
         init.activeLimit1 = rpmRtn.activeLimit;
         init.activeLimit2 = rpmRtn.activeLimit;
         init.activeLimit3 = rpmRtn.activeLimit;
         init.actualPPH1 = nonStop * eff;
         init.actualPPH2 = nonStop * eff;
         init.actualPPH3 = nonStop * eff;
         init.actualPPHperShift1 = nonStop * eff * 8;
         init.actualPPHperShift2 = nonStop * eff * 8;
         init.actualPPHperShift3 = nonStop * eff * 8;
         init.balloonLimit1 = bLimit;
         init.balloonLimit2 = bLimit;
         init.balloonLimit3 = bLimit;
         init.cableCap1 = cap;
         init.cableCap2 = cap;
         init.cableCap3 = cap;
         init.conf1 = conf;
         init.conf2 = conf;
         init.conf3 = conf;
         init.denier1 = den;
         init.denier2 = den;
         init.denier3 = den;
         init.efficiency1 = eff;
         init.efficiency2 = eff;
         init.efficiency3 = eff;
         init.isCapped1 = rpmRtn.isCapped;
         init.isCapped2 = rpmRtn.isCapped;
         init.isCapped3 = rpmRtn.isCapped;
         init.nonStopPPH1 = nonStop;
         init.nonStopPPH2 = nonStop;
         init.nonStopPPH3 = nonStop;
         init.ply1 = ply;
         init.ply2 = ply;
         init.ply3 = ply;
         init.rpmBase1 = rpmRtn.physics;
         init.rpmBase2 = rpmRtn.physics;
         init.rpmBase3 = rpmRtn.physics;
         init.rpmFinal1 = rpmFinal;
         init.rpmFinal2 = rpmFinal;
         init.rpmFinal3 = rpmFinal;
         init.spindles1 = spindles;
         init.spindles2 = spindles;
         init.spindles3 = spindles;
         init.takeUpSpeed1 = takeUp;
         init.takeUpSpeed2 = takeUp;
         init.takeUpSpeed3 = takeUp;
         init.tensioner1 = tensionType;
         init.tensioner2 = tensionType;
         init.tensioner3 = tensionType;
         init.totalDenier1 = totalDen;
         init.totalDenier2 = totalDen;
         init.totalDenier3 = totalDen;
         init.totalDenierContracted1 = totalDen * init.twistContraction;
         init.totalDenierContracted2 = totalDen * init.twistContraction;
         init.totalDenierContracted3 = totalDen * init.twistContraction;
         init.totalPPH1 = nonStop * eff * spindles;
         init.totalPPH2 = nonStop * eff * spindles;
         init.totalPPH3 = nonStop * eff * spindles;
         init.tpi1 = tpi;
         init.tpi2 = tpi;
         init.tpi3 = tpi;
         init.tpm1 = tpm;
         init.tpm2 = tpm;
         init.tpm3 = tpm;
         init.twistContraction = contraction;
         init.yardsPerPound1 = yardsLb;
         init.yardsPerPound2 = yardsLb;
         init.yardsPerPound3 = yardsLb;
         setSteps(init);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [model])

   function GetTwistDefaults() {
      setSpinner(true);
      api
         .fetch(`Engineering/GetTwistDefaults`)
         .then((response) => {
            if (response?.data) {
               if (response.data.success) {
                  setDefaults(response.data.defaults);
               } else {
                  toast.error(response.data.message);
               }
            }
            else {
               toast.error("Unable to retrieve default values!");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
         });
   }

   const handleFocus = (event) => event.target.select();

   function onChangeStep1(field, value) {
      if (field === "denier" && value > 15000) {
         toast.warn("Base yarn cannot exceed 15,000");
         value = 15000;
      }
      else if (field === "efficiency" && value > 1.0) {
         toast.warn("Efficiency cannot exceed 1.0 (100%)");
         value = 1.0;
      }
      else if (field === "ply")
         value = validatePly(value);
      else if (field === "tpi")
         value = validateTPI(value);
      else if (field === "spindles")
         value = validateSpindles(value);

      let changes = Object.assign({}, steps)
      let den = (field === "denier") ? value : changes.denier1;
      let eff = (field === "efficiency") ? value : changes.efficiency1;
      let ply = (field === "ply") ? value : changes.ply1;
      let tpi = (field === "tpi") ? value : changes.tpi1;
      let spindles = (field === "spindles") ? value : changes.spindles1;
      let tpm = tpi * IN_PER_M;
      let totalDen = den * ply;
      let totalDenCont = totalDen * changes.twistContraction;
      let yardsLb = YARDS_FACTOR / totalDenCont;
      let tensionTyp = ply > 1 ? "flyer" : "single";
      let rpmRtn = rpmResult(tensionTyp, den, totalDen, tpi, ply, mode);
      let rpmFinal = rpmRtn.final;
      let takeUp = ((2 * rpmFinal) / tpm) / 1.094;
      let nonStop = (takeUp * 60) / yardsLb;

      changes.activeLimit1 = rpmRtn.activeLimit;
      changes.actualPPH1 = nonStop * eff;
      changes.actualPPHperShift1 = nonStop * eff * 8;
      changes.balloonLimit1 = Number.isFinite(rpmRtn.limBalloon) ? rpmRtn.limBalloon.toFixed(0) : "-";
      changes.cableCap1 = Number.isFinite(rpmRtn.limCable) && rpmRtn.limCable < 1e9 ? rpmRtn.limCable.toFixed(0) : "-";
      changes.conf1 = confidence(rpmRtn);
      changes.denier1 = den;
      changes.efficiency1 = eff;
      changes.isCapped1 = rpmRtn.isCapped;
      changes.nonStopPPH1 = nonStop;
      changes.ply1 = ply;
      changes.rpmBase1 = rpmRtn.physics;
      changes.rpmFinal1 = rpmFinal;
      changes.spindles1 = spindles;
      changes.takeUpSpeed1 = takeUp;
      changes.tensioner1 = tensionTyp;
      changes.totalDenier1 = totalDen;
      changes.totalDenierContracted1 = totalDenCont;
      changes.totalPPH1 = nonStop * eff * spindles;
      changes.tpi1 = tpi;
      changes.tpm1 = tpm;
      changes.yardsPerPound1 = YARDS_FACTOR / totalDenCont;
      setSteps(changes);
   }

   function onChangeStep2(field, value) {
      if (field === "denier" && value > 15000) {
         toast.warn("Base yarn cannot exceed 15,000");
         value = 15000;
      }
      else if (field === "efficiency" && value > 1.0) {
         toast.warn("Efficiency cannot exceed 1.0 (100%)");
         value = 1.0;
      }
      else if (field === "ply")
         value = validatePly(value);
      else if (field === "tpi")
         value = validateTPI(value);
      else if (field === "spindles")
         value = validateSpindles(value);

      let changes = Object.assign({}, steps)
      if (field === "active2") {
         changes[field] = value;
      }
      else {
         let den = (field === "denier") ? value : changes.denier2;
         let eff = (field === "efficiency") ? value : changes.efficiency2;
         let ply = (field === "ply") ? value : changes.ply2;
         let tpi = (field === "tpi") ? value : changes.tpi2;
         let spindles = (field === "spindles") ? value : changes.spindles2;
         let tpm = tpi * IN_PER_M;
         let totalDen = den * ply;
         let totalDenCont = totalDen * changes.twistContraction;
         let yardsLb = YARDS_FACTOR / totalDenCont;
         let tensionTyp = ply > 1 ? "flyer" : "single";
         let rpmRtn = rpmResult(tensionTyp, den, totalDen, tpi, ply, mode);
         let rpmFinal = rpmRtn.final;
         let takeUp = ((2 * rpmFinal) / tpm) / 1.094;
         let nonStop = (takeUp * 60) / yardsLb;

         changes.activeLimit2 = rpmRtn.activeLimit;
         changes.actualPPH2 = nonStop * eff;
         changes.actualPPHperShift2 = nonStop * eff * 8;
         changes.balloonLimit2 = Number.isFinite(rpmRtn.limBalloon) ? rpmRtn.limBalloon.toFixed(0) : "-";
         changes.cableCap2 = Number.isFinite(rpmRtn.limCable) && rpmRtn.limCable < 1e9 ? rpmRtn.limCable.toFixed(0) : "-";
         changes.conf2 = confidence(rpmRtn);
         changes.denier2 = den;
         changes.efficiency2 = eff;
         changes.isCapped2 = rpmRtn.isCapped;
         changes.nonStopPPH2 = nonStop;
         changes.ply2 = ply;
         changes.rpmBase2 = rpmRtn.physics;
         changes.rpmFinal2 = rpmFinal;
         changes.spindles2 = spindles;
         changes.takeUpSpeed2 = takeUp;
         changes.tensioner2 = tensionTyp;
         changes.totalDenier2 = totalDen;
         changes.totalDenierContracted2 = totalDenCont;
         changes.totalPPH2 = nonStop * eff * spindles;
         changes.tpi2 = tpi;
         changes.tpm2 = tpm;
         changes.yardsPerPound2 = YARDS_FACTOR / totalDenCont;
      }
      setSteps(changes);
   }

   function onChangeStep3(field, value) {
      if (field === "denier" && value > 15000) {
         toast.warn("Base yarn cannot exceed 15,000");
         value = 15000;
      }
      else if (field === "efficiency" && value > 1.0) {
         toast.warn("Efficiency cannot exceed 1.0 (100%)");
         value = 1.0;
      }
      else if (field === "ply")
         value = validatePly(value);
      else if (field === "tpi")
         value = validateTPI(value);
      else if (field === "spindles")
         value = validateSpindles(value);

      let changes = Object.assign({}, steps)
      if (field === "active3") {
         changes[field] = value;
      }
      else {
         let den = (field === "denier") ? value : changes.denier3;
         let eff = (field === "efficiency") ? value : changes.efficiency3;
         let ply = (field === "ply") ? value : changes.ply3;
         let tpi = (field === "tpi") ? value : changes.tpi3;
         let spindles = (field === "spindles") ? value : changes.spindles3;
         let tpm = tpi * IN_PER_M;
         let totalDen = den * ply;
         let totalDenCont = totalDen * changes.twistContraction;
         let yardsLb = YARDS_FACTOR / totalDenCont;
         let tensionTyp = ply > 1 ? "flyer" : "single";
         let rpmRtn = rpmResult(tensionTyp, den, totalDen, tpi, ply, mode);
         let rpmFinal = rpmRtn.final;
         let takeUp = ((2 * rpmFinal) / tpm) / 1.094;
         let nonStop = (takeUp * 60) / yardsLb;

         changes.activeLimit3 = rpmRtn.activeLimit;
         changes.actualPPH3 = nonStop * eff;
         changes.actualPPHperShift3 = nonStop * eff * 8;
         changes.balloonLimit3 = Number.isFinite(rpmRtn.limBalloon) ? rpmRtn.limBalloon.toFixed(0) : "-";
         changes.cableCap3 = Number.isFinite(rpmRtn.limCable) && rpmRtn.limCable < 1e9 ? rpmRtn.limCable.toFixed(0) : "-";
         changes.conf3 = confidence(rpmRtn);
         changes.denier3 = den;
         changes.efficiency3 = eff;
         changes.isCapped3 = rpmRtn.isCapped;
         changes.nonStopPPH3 = nonStop;
         changes.ply3 = ply;
         changes.rpmBase3 = rpmRtn.physics;
         changes.rpmFinal3 = rpmFinal;
         changes.spindles3 = spindles;
         changes.takeUpSpeed3 = takeUp;
         changes.tensioner3 = tensionTyp;
         changes.totalDenier3 = totalDen;
         changes.totalDenierContracted3 = totalDenCont;
         changes.totalPPH3 = nonStop * eff * spindles;
         changes.tpi3 = tpi;
         changes.tpm3 = tpm;
         changes.yardsPerPound3 = YARDS_FACTOR / totalDenCont;
      }
      setSteps(changes);
   }

   function validatePly(val) {
      if (val < 1) 
         toast.warn("Ply must be at least 1");
      else if (val > 99)
         toast.warn("Ply cannot exceed 2 digits");

      val = Math.max(1, helpers.roundNum(val));
      return clamp(val, 1, 99);
   }

   function validateSpindles(val) {
      if (val < 1)
         toast.warn("Spindles must be at least 1");
      else if (val > 999)
         toast.warn("Spindles cannot exceed 3 digits");

      val = Math.max(1, helpers.roundNum(val));
      return clamp(val, 1, 999);
   }

   function validateTPI(val) {
      if (val < 0.8) 
         toast.warn("TPI must be at least 0.8");
      else if (val > 13.0) 
         toast.warn("TPI cannot exceed 13.0");

      return clamp(val, 0.8, 13.0);;
   }

   return (
      <Container fluid>
         <Row>
            <Col>
               <Fragment>
                  <ServiceThreadSpinner on={spin}>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           <Label className="noWrap h5">Enter appropriate values below to see calculation results<br />All calculations assume a {helpers.formatPercent(pct, 1)} twist contraction</Label>
                           <br /><br />
                           <div>
                              {/* Activation Check Boxes */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px"></Col>
                                 <Col className="form-check form-check-inline data-cell noWrap px-3 width175px">
                                    <Input
                                       className="mx-0"
                                       type="checkbox"
                                       name="active1"
                                       checked={steps.active1}
                                       disabled={true}
                                    />
                                    <Label for="active1">&nbsp;&nbsp;&nbsp;Show</Label>
                                 </Col>
                                 <Col className="form-check form-check-inline noWrap mx-0 px-0 width175px">
                                    <Input
                                       className="mx-0 px-0"
                                       type="checkbox"
                                       name="active2"
                                       checked={steps.active2}
                                       onChange={(e) => onChangeStep2("active2", !steps.active2)}
                                    />
                                    <Label for="active2">&nbsp;&nbsp;&nbsp;Show</Label>
                                 </Col>
                                 <Col className="form-check form-check-inline noWrap mx-0 px-0 width175px">
                                    <Input
                                       className="mx-0 px-0"
                                       type="checkbox"
                                       name="active3"
                                       checked={steps.active3}
                                       onChange={(e) => onChangeStep3("active3", !steps.active3)}
                                    />
                                    <Label for="active3">&nbsp;&nbsp;&nbsp;Show</Label>
                                 </Col>
                              </Row>

                              {/* Step Header Rows */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">PARAMETER</Col>
                                 <Col className="data-cell noWrap h6 width175px">STEP 1</Col>
                                 <Col className="data-cell noWrap h6 width175px">STEP 2</Col>
                                 <Col className="data-cell noWrap h6 width175px">STEP 3</Col>
                              </Row>

                              {/* Denier Inputs */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Base Yarn (Dn):</Col>
                                 <Col className="data-cell noWrap h6 width175px">
                                    <Input
                                       type="number"
                                       name="denier"
                                       value={steps?.denier1 || "0"}
                                       className="h-100 py-1 no-spinner width125px"
                                       onChange={(e) => { onChangeStep1("denier", e.target.value) }}
                                       maxLength={5}
                                       max={15000}
                                       min={420}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="denier2"
                                          value={steps?.denier2 || "0"}
                                          className="h-100 py-1 no-spinner width125px"
                                          onChange={(e) => { onChangeStep2("denier", e.target.value) }}
                                          maxLength={5}
                                          max={15000}
                                          min={420}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="denier3"
                                          value={steps?.denier3 || "0"}
                                          className="h-100 py-1 no-spinner width125px"
                                          onChange={(e) => { onChangeStep3("denier", e.target.value) }}
                                          maxLength={5}
                                          max={15000}
                                          min={420}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Ply Inputs */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Plies:</Col>
                                 <Col className="data-cell noWrap h6 width175px">
                                    <Input
                                       type="number"
                                       name="ply"
                                       value={steps?.ply1 || "0"}
                                       className="h-100 py-1 no-spinner width80px"
                                       onChange={(e) => { onChangeStep1("ply", e.target.value) }}
                                       maxLength={2}
                                       max={99}
                                       min={1}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="ply2"
                                          value={steps?.ply2 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep2("ply", e.target.value) }}
                                          maxLength={2}
                                          max={99}
                                          min={1}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="ply3"
                                          value={steps?.ply3 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep3("ply", e.target.value) }}
                                          maxLength={2}
                                          max={99}
                                          min={1}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* TPI Inputs */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Twist (TPI):</Col>
                                 <Col className="data-cell noWrap h6 width175px">
                                    <Input
                                       type="number"
                                       name="tpi"
                                       value={steps?.tpi1 || "0"}
                                       className="h-100 py-1 no-spinner width80px"
                                       onChange={(e) => { onChangeStep1("tpi", e.target.value) }}
                                       maxLength={5}
                                       max={13.00}
                                       min={1.00}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="tpi2"
                                          value={steps?.tpi2 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep2("tpi", e.target.value) }}
                                          maxLength={5}
                                          max={13.00}
                                          min={1.00}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="tpi3"
                                          value={steps?.tpi3 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep3("tpi", e.target.value) }}
                                          maxLength={5}
                                          max={13.00}
                                          min={1.00}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Efficiency Inputs */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Efficiency:</Col>
                                 <Col className="data-cell noWrap h6 width175px">
                                    <Input
                                       type="number"
                                       name="efficiency"
                                       value={steps?.efficiency1 || "0"}
                                       className="h-100 py-1 no-spinner width80px"
                                       onChange={(e) => { onChangeStep1("efficiency", e.target.value) }}
                                       maxLength={4}
                                       max={1.00}
                                       min={0.01}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="efficiency2"
                                          value={steps?.efficiency2 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep2("efficiency", e.target.value) }}
                                          maxLength={4}
                                          max={1.00}
                                          min={0.01}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="efficiency3"
                                          value={steps?.efficiency3 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep3("efficiency", e.target.value) }}
                                          maxLength={4}
                                          max={1.00}
                                          min={0.01}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Spindle Count Inputs */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Spindle Count:</Col>
                                 <Col className="data-cell noWrap h6 width175px">
                                    <Input
                                       type="number"
                                       name="spindles"
                                       value={steps?.spindles1 || "0"}
                                       className="h-100 py-1 no-spinner width80px"
                                       onChange={(e) => { onChangeStep1("spindles", e.target.value) }}
                                       maxLength={3}
                                       max={999}
                                       min={1}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="spindles2"
                                          value={steps?.spindles2 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep2("spindles", e.target.value) }}
                                          maxLength={3}
                                          max={999}
                                          min={1}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="data-cell noWrap h6 width175px">
                                       <Input
                                          type="number"
                                          name="spindles3"
                                          value={steps?.spindles3 || "0"}
                                          className="h-100 py-1 no-spinner width80px"
                                          onChange={(e) => { onChangeStep3("spindles", e.target.value) }}
                                          maxLength={3}
                                          max={999}
                                          min={1}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Row Breaks */}
                              <Row className="h-100"><hr /></Row>

                              {/* Tensioner Types */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Tensioner Type:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {steps.tensioner1}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.tensioner2}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.tensioner3}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Total Deniers */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Total Denier:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.totalDenierContracted1)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.totalDenierContracted2)}
                                    </Col>
                                 ): <Col className = "data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.totalDenierContracted3)}
                                    </Col>
                                 ): <Col className = "data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Yards per Pounds */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Yards/Lb:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.yardsPerPound1)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.yardsPerPound2)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.yardsPerPound3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Physics RPMs */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Physics RPM:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.rpmBase1)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.rpmBase2)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.rpmBase3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Final RPM Speeds */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">FINAL RPM:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.rpmFinal1)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.rpmFinal2)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.rpmFinal3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Take-Up Speeds */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Take-Up Speed:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.takeUpSpeed1, 2)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.takeUpSpeed2, 2)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.takeUpSpeed3, 2)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Non-Stop PPH per Spindle */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Non-Stop PPH/Spdl:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.nonStopPPH1, 3)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.nonStopPPH2, 3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.nonStopPPH3, 3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Actual PPH per Spindle */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Actual PPH/Spdl:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.actualPPH1, 3)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.actualPPH2, 3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.actualPPH3, 3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Actual PPH per Spindle per Shift */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Actual PPH/Spdl/Shift:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.actualPPHperShift1, 3)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.actualPPHperShift2, 3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.actualPPHperShift3, 3)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Total PPH per Spindle Count */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Total PPH/Spindle Count:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {helpers.formatNumberWithThousands(steps.totalPPH1, 2)}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.totalPPH2, 2)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {helpers.formatNumberWithThousands(steps.totalPPH3, 2)}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Row Breaks */}
                              <Row className="h-100"><hr /></Row>

                              {/* Confidence Badges */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Confidence:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    <Badge level={steps.conf1.level} text={steps.conf1.label} />
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       <Badge level={steps.conf2.level} text={steps.conf2.label} />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       <Badge level={steps.conf3.level} text={steps.conf3.label} />
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Active Limits */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Active Limit:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                   {steps.activeLimit1}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.activeLimit2}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.activeLimit3}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Balloon Limits */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Balloon Limit:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {steps.balloonLimit1}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.balloonLimit2}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.balloonLimit3}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>

                              {/* Cable Caps */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width200px">Cable cap (plies &ge; 5):</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width175px">
                                    {steps.cableCap1}
                                 </Col>
                                 {steps?.active2 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.cableCap2}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                                 {steps?.active3 === true ? (
                                    <Col className="d-flex align-items-center noWrap h6 width175px">
                                       {steps.cableCap3}
                                    </Col>
                                 ) : <Col className="data-cell noWrap h6 width175px"></Col>}
                              </Row>
                           </div>
                        </Col>
                     </Row>
                  </ServiceThreadSpinner>
               </Fragment>
            </Col>
         </Row>
      </Container>
   );
}
